import { apiUrl } from '../config/api'
import { refreshToken } from './tokens'

const getToken = () => {
  const raw = window.localStorage.getItem('authToken')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && parsed.rawData) return parsed.rawData
    if (typeof parsed === 'string') return parsed
    return null
  } catch {
    return raw
  }
}

const saveToken = (rawToken) => {
  window.localStorage.setItem('authToken', JSON.stringify(rawToken))
}

let isRefreshing = false
let refreshPromise = null

async function attemptRefresh() {
  if (isRefreshing) return refreshPromise

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const expiredToken = getToken()
      if (!expiredToken) return null

      const data = await refreshToken(expiredToken)
      const newRawToken =
        typeof data === 'string'
          ? data.replace(/^"|"$/g, '')
          : data?.rawData ?? data?.token ?? null

      if (!newRawToken) return null
      saveToken(newRawToken)
      return newRawToken
    } catch (e) {
      console.error('Token refresh failed', e)
      return null
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

async function fetchWithAuth(url, options = {}) {
  const token = getToken()

  const makeHeaders = (t) => ({
    'Content-Type': 'application/json',
    ...options.headers,
    Authorization: `Bearer ${t}`
  })
console.log('Fetching', url, 'with token', token)
  let res = await fetch(url, { ...options, headers: makeHeaders(token) })

  if (res.status === 401) {
    const newToken = await attemptRefresh()

    if (!newToken) {
      window.localStorage.clear()
      window.location.href = '/SignIn'
      return null
    }

    res = await fetch(url, { ...options, headers: makeHeaders(newToken) })
  }

  return res
}

async function sendReq(params, url) {
  const res = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(params)
  })
  return res?.json()
}

async function sendMethod(params, url, method) {
  const res = await fetchWithAuth(url, {
    method,
    body: JSON.stringify(params)
  })
  return res?.json()
}

async function sendGet(url) {
  const res = await fetchWithAuth(url, { method: 'GET' })
  return res?.json()
}

async function sendDelete(url) {
  const res = await fetchWithAuth(url, { method: 'DELETE' })
  return res?.json()
}

// ─── Rules ────────────────────────────────────────────────────────────────────

export async function getRulesByDocId(documentId) {
  return sendGet(
    apiUrl('GetRules') + `&type=doc_alert&documentId=${documentId}`
  )
}

export async function getRuleById(id) {
  return sendGet(apiUrl('GetRuleById') + `&id=${id}`)
}

export async function insertRule(params) {
  return sendReq(params, apiUrl('InsertRule'))
}

export async function updateRule(id, params) {
  return sendMethod(params, apiUrl('UpdateRule') + `&id=${id}`, 'PUT')
}

export async function deleteRule(id) {
  return sendDelete(apiUrl('DeleteRule') + `&id=${id}`)
}

// ─── Schedules ────────────────────────────────────────────────────────────────

export async function insertRuleSchedule(ruleId, params) {
  return sendReq(params, apiUrl('InsertRuleSchedule') + `&ruleId=${ruleId}`)
}

export async function updateRuleSchedule(id, params) {
  return sendMethod(params, apiUrl('UpdateRuleSchedule') + `&id=${id}`, 'PUT')
}

export async function deleteRuleSchedule(id) {
  return sendDelete(apiUrl('DeleteRuleSchedule') + `&id=${id}`)
}

// ─── Recipients ───────────────────────────────────────────────────────────────

export async function getRuleRecipients(ruleId) {
  return sendGet(apiUrl('GetRuleRecipients') + `&ruleId=${ruleId}`)
}

export async function insertRuleRecipient(ruleId, params) {
  return sendReq(params, apiUrl('InsertRuleRecipient') + `&ruleId=${ruleId}`)
}

export async function deleteRuleRecipient(id) {
  return sendDelete(apiUrl('DeleteRuleRecipient') + `&id=${id}`)
}

export async function getTemplates() {
  return sendGet(apiUrl('GetTemplates') + `&brandLabel=${window.$brand}`)
}

export async function getTemplatesByUser(userId) {
  return sendGet(
    apiUrl('GetTemplatesByUser') +
      `&brandLabel=${window.$brand}&userId=${userId}`
  )
}

export async function getTemplatesByUserAndDocument(userId, documentId) {
  return sendGet(
    apiUrl('GetTemplatesByUserAndDocument') +
      `&brandLabel=${window.$brand}&userId=${userId}&documentId=${documentId}`
  )
}

export async function getTemplateById(id) {
  return sendGet(
    apiUrl('GetTemplateById') + `&brandLabel=${window.$brand}&id=${id}`
  )
}

export async function insertTemplate(params) {
  return sendReq(
    { ...params, brandLabel: window.$brand },
    apiUrl('InsertTemplate')
  )
}

export async function updateTemplate(id, params) {
  return sendMethod(params, apiUrl('UpdateTemplate') + `&id=${id}`, 'PUT')
}

export async function upsertTemplateTranslation(templateId, locale, params) {
  return sendMethod(
    params,
    apiUrl('UpsertTemplateTranslation') +
      `&templateId=${templateId}&locale=${locale}`,
    'PUT'
  )
}

export async function deleteTemplate(id) {
  return sendDelete(apiUrl('DeleteTemplate') + `&id=${id}`)
}

// ─── Reminders ────────────────────────────────────────────────────────────────
// A reminder is:
//   notification_rule_templates  (type='reminder')
//   notification_rules           (type='scheduled', document_id=NULL)
//   calendar_events              (rule_id, rrule, starts_at, recurrence_end)
//   notification_template_translations  (shared, same as templates)
//
// The frontend orchestrates the multi-step create/update using existing functions
// plus the two new calendar event functions below.
// Translations reuse upsertTemplateTranslation — same table, same function.

/**
 * List all scheduled reminders.
 * Returns joined rows: rule_id, template_id, description, is_active,
 * rrule, starts_at, recurrence_end, has_en, has_es
 */
export async function getReminders() {
  return sendGet(apiUrl('GetReminders') + `&brandLabel=${window.$brand}`)
}

/**
 * Single reminder with full translations[].
 * @param {number} ruleId  notification_rules.id
 */
export async function getReminderById(ruleId) {
  return sendGet(apiUrl('GetReminderById') + `&id=${ruleId}`)
}

/**
 * Insert a calendar_event row for a reminder rule.
 * @param {number} ruleId
 * @param {{ rrule: string, starts_at: string|null, recurrence_end: string|null }} params
 */
export async function insertCalendarEvent(ruleId, params) {
  return sendReq(params, apiUrl('InsertCalendarEvent') + `&ruleId=${ruleId}`)
}

/**
 * Update a calendar_event row.
 * @param {number} calendarEventId  calendar_events.id
 * @param {{ rrule: string, starts_at: string|null, recurrence_end: string|null }} params
 */
export async function updateCalendarEvent(calendarEventId, params) {
  return sendMethod(
    params,
    apiUrl('UpdateCalendarEvent') + `&id=${calendarEventId}`,
    'PUT'
  )
}

/**
 * Delete a calendar_event row by its id.
 * Called when deleting a reminder (after soft-deleting the rule).
 * @param {number} calendarEventId  calendar_events.id
 */
export async function deleteCalendarEvent(calendarEventId) {
  return sendDelete(apiUrl('DeleteCalendarEvent') + `&id=${calendarEventId}`)
}

// ─── Notification log ─────────────────────────────────────────────────────────

export async function getNotificationLog(ruleId) {
  return sendGet(apiUrl('GetNotificationLog') + `&ruleId=${ruleId}`)
}

// ─── Doc Alert Calendar Events ────────────────────────────────────────────────

/**
 * All active doc_alert rules for the calendar — brand-scoped, no visibility filter.
 * For BLP admins / super admins.
 */
export async function getDocAlertEvents() {
  return sendGet(
    apiUrl('GetDocAlertEvents') + `&brandLabel=${window.$brand}`
  )
}

/**
 * All active doc_alert rules for the calendar — client-scoped, visible=1 only.
 * For client users.
 * @param {number} clientId
 */
export async function getDocAlertEventsByClient(clientId) {
  return sendGet(
    apiUrl('ListDocAlertsByClient') + `&clientId=${clientId}`
  )
}

/* Expirations */
export async function getExpirationsByClient(clientId) {
  return sendGet(
    apiUrl('ListExpirationsByClient') + `&clientId=${clientId}`
  )
}